import { cloneDeep } from 'lodash-es';
import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useOutletContext } from 'react-router-dom-v5-compat';

import { Box } from '~/eds/src/components/Box/Box';
import { Card } from '~/eds/src/components/Card/Card';
import { CollapsibleText } from '~/eds/src/components/CollapsibleText/CollapsibleText';
import { ContentContainer } from '~/eds/src/components/ContentContainer/ContentContainer';
import { EmptyPage } from '~/eds/src/components/EmptyPage/EmptyPage';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Text } from '~/eds/src/components/Text/Text';
import { UserAction } from '~/eds/src/types/UserAction';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { api } from '~/redux/api/createApi';
import {
  ClauseData as ClauseDataType,
  VariationData as VariationDataType,
} from '~/redux/api/methods/clauseLibrary';
import { slices } from '~/redux/slices';
import { useRouting } from '~/routing/useRouting';
import HtmlContent from '~/ui/components/HtmlContent/HtmlContent';
import { htmlToText } from '~/utils/strings';
import { testIsSuperAdmin } from '~/utils/user';

import ClauseVariation from '../ClauseVariation/ClauseVariation';
import { CLAUSE_LIBRARY_CHAR_DISPLAY_LIMIT } from '../constants';
import { sortVariationsByPositionAndName } from '../utils';

interface ClauseViewContext {
  editText: string;
  deleteText: string;
  hasEditPermission: boolean;
  handleCreateClauseVariation: (clauseId: string, clauseName: string) => void;
  handleEditClause: (clauseId: string) => void;
  handleDeleteClause: (clauseId: string, clauseName: string) => void;
  handleDeleteVariation: (
    variationId: string,
    variationName: string,
    clauseId: string,
  ) => void;
}

export const ClauseView = () => {
  const {
    editText,
    deleteText,
    hasEditPermission,
    handleCreateClauseVariation,
    handleEditClause,
    handleDeleteClause,
    handleDeleteVariation,
  } = useOutletContext<ClauseViewContext>();
  const intl = useIntl();
  const currentUser = useCurrentUser();
  const { toast } = useToast();
  const selectedClause = useSelector(
    slices.clauseLibrary.selectors.selectSelectedClause,
  );
  const filters = useSelector(slices.clauseLibrary.selectors.selectFilters);

  const { params } = useRouting();
  const { clauseId } = params as { clauseId: string };
  const {
    data: clauseData,
    error: clauseDataError,
    isLoading: clauseDataLoading,
  } = api.endpoints.clauseLibraryGetClause.useQuery(
    { clauseId, filters },
    { skip: !clauseId },
  );

  useEffect(() => {
    if (clauseDataError && !filters?.query && !filters?.chips) {
      toast({
        status: 'danger',
        message: intl.formatMessage(
          {
            id: 'ClauseLibrary.loadClauseErrorWithId',
            defaultMessage: 'Failed to load clause id {clauseId}',
            description:
              'Error message when specific clause fails to load. Example: clauseId = "123"',
          },
          { clauseId },
        ),
      });
    }
  }, [clauseDataError]);

  const canEdit = testIsSuperAdmin(currentUser) || hasEditPermission;

  const clauseVariations = (clause: ClauseDataType) => {
    if (clause?.variations?.length) {
      // clauseData is not extensible, necessitating deep copy for sorting
      const clauseCopy: ClauseDataType = cloneDeep(clause);
      clauseCopy?.variations?.sort(sortVariationsByPositionAndName);
      return clauseCopy?.variations?.map((variation: VariationDataType) => (
        <ClauseVariation
          canEdit={canEdit}
          key={variation.key}
          variation={variation}
          clauseId={clauseCopy.key}
          handleDelete={handleDeleteVariation}
        />
      ));
    }
    return (
      <EmptyPage
        preset={
          canEdit ? 'no-clause-variations-admin' : 'no-clause-variations-user'
        }
      />
    );
  };

  const clauseHeaderActions: UserAction[] = [
    {
      icon: 'plus',
      mode: 'icon',
      level: 'tertiary',
      text: intl.formatMessage({
        id: 'ClauseLibrary.addVariationHeaderAction',
        defaultMessage: 'Add clause variation',
        description: 'Add clause variation header action text',
      }),
      tooltip: intl.formatMessage({
        id: 'ClauseLibrary.addVariationTooltip',
        defaultMessage: 'Add Clause Variation',
        description: 'Add clause variation tooltip',
      }),
      onClick: () =>
        handleCreateClauseVariation(
          selectedClause?.key ?? '',
          selectedClause?.name ?? '',
        ),
    },
  ];

  const clauseHeaderMoreActions = [
    {
      text: editText,
      onClick: () => handleEditClause(selectedClause?.key ?? ''),
    },
    {
      text: deleteText,
      onClick: () =>
        handleDeleteClause(
          selectedClause?.key ?? '',
          selectedClause?.name ?? '',
        ),
    },
  ];

  const placeHolderContent = clauseDataError
    ? {
        message: intl.formatMessage(
          {
            id: 'ClauseLibrary.loadClauseError',
            defaultMessage: 'Failed to load clause',
            description:
              'Error message when specific clause fails to load. Example: clauseId = "123"',
          },
          { clauseId },
        ),
      }
    : undefined;

  return (
    <Layout
      aria-label={intl.formatMessage({
        id: 'ClauseLibrary.clauseContentLabel',
        defaultMessage: 'Clause Content',
        description: 'Aria label for clause content area',
      })}
      position="relative"
      overflowY="auto"
      minH="100%"
      w="100%"
      minW="0"
      flex="1"
    >
      <ContentContainer
        loadingContent={{
          isLoading: clauseDataLoading,
        }}
        placeholderContent={placeHolderContent}
      >
        <Layout direction="column" w="100%">
          <Card
            header={
              !!selectedClause?.name
                ? {
                    title: selectedClause.name,
                    actions: canEdit ? clauseHeaderActions : undefined,
                    moreActions: canEdit ? clauseHeaderMoreActions : undefined,
                  }
                : undefined
            }
            mode="embedded"
            name={intl.formatMessage({
              id: 'ClauseLibrary.listClausesCardName',
              defaultMessage: 'List Clauses',
              description: 'Name for list clauses card',
            })}
            role="list"
          >
            {clauseData && !!clauseData.guidance && (
              <Box styles={componentStyles.wrap} pt={3} px={6}>
                <CollapsibleText
                  text={htmlToText(clauseData.guidance)}
                  fullContent={
                    <Text>
                      <HtmlContent html={clauseData.guidance} />
                    </Text>
                  }
                  limit={CLAUSE_LIBRARY_CHAR_DISPLAY_LIMIT}
                  mode="icon"
                />
              </Box>
            )}
            {clauseData && clauseVariations(clauseData)}
          </Card>
        </Layout>
      </ContentContainer>
    </Layout>
  );
};

const componentStyles = {
  wrap: {
    wordWrap: 'break-word',
    hyphens: 'auto',
  },
};
